import { getMeOptions, getOnboardingSessionOptions } from '@labflow/api-client';
import { FlowField, LandingHeadline, ScrollArea } from '@labflow/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';

import {
  CONSENT_ROUTE,
  FALLBACK_ROUTE,
  isOnboardingRoute,
} from '../features/onboarding/require-onboarding';
import {
  LandingClosing,
  LandingComingModules,
  LandingDailyFlow,
  LandingFooter,
} from '../features/platform/landing-sections';
import { LandingStart } from '../features/platform/landing-start';
import { serverFeaturesEnabled } from '../lib/server-features';

export const Route = createFileRoute('/')({
  beforeLoad: async ({ context }) => {
    if (!serverFeaturesEnabled) return;

    // D86 AC-PF-01-13: 로그인하지 않았거나 `/me` 조회가 실패하면 공개 랜딩을 그대로 보여준다 —
    // 사이트의 첫 화면이 일시적 API 오류로 막혀서는 안 된다.
    const me = await context.queryClient
      .ensureQueryData({ ...getMeOptions(), retry: false })
      .catch(() => null);
    if (!me) return;

    // D113 AC-PF-01-16: 필수 동의가 없으면 온보딩 완료 여부와 무관하게 동의 화면이 목적지다.
    if (me.consent_required) throw redirect({ to: CONSENT_ROUTE, replace: true });

    // D93: 완료 사용자는 헤더 로고를 포함해 어떤 경로로 `/`에 와도 My Lab으로 보낸다.
    if (me.onboarding_completed_at) {
      throw redirect({ to: '/mylab', replace: true });
    }

    const session = await context.queryClient.ensureQueryData(getOnboardingSessionOptions());
    const nextRoute = isOnboardingRoute(session.next_route) ? session.next_route : FALLBACK_ROUTE;
    throw redirect({ to: nextRoute, replace: true });
  },
  component: Landing,
});

/** 공개 랜딩 — D154. 히어로(헤드라인 · 한 줄 가치 · 온보딩 시작) 아래에 제품을 이해시키는 구획을 둔다. */
function Landing() {
  return (
    <ScrollArea className="h-full">
      <section aria-labelledby="landing-headline" className="relative isolate overflow-hidden">
        <FlowField className="-z-10" />
        <div className="px-gutter gap-panel ease-flow duration-slow md:pb-frame flex flex-col items-start pt-16 pb-8 transition-[opacity,translate] md:pt-28 starting:opacity-0 motion-safe:starting:translate-y-2">
          <LandingHeadline id="landing-headline" className="justify-start" />
          <div className="gap-row flex max-w-2xl flex-col">
            <p className="font-display text-fg text-2xl">모든 연구를 하나의 흐름으로</p>
            <p className="text-ink text-lg">
              실험 기록이 랩미팅 안건이 되고, 읽을 논문과 학회 마감이 같은 자리에서 이어져요.
            </p>
          </div>
          <div className="pt-snug">
            <LandingStart />
          </div>
        </div>
      </section>

      <LandingDailyFlow />
      <LandingComingModules />
      <LandingClosing />
      <LandingFooter />
    </ScrollArea>
  );
}
